import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Wallet, Send } from "lucide-react";
import { api, apiError, fmtDate, fmtHours } from "@/lib/api";
import { StatusBadge, KindBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";

const today = new Date().toISOString().slice(0, 10);

export default function Absences() {
  const { user, pharmacy } = useAuth();
  const types = pharmacy?.absence_types || [];
  const [list, setList] = useState([]);
  const [balance, setBalance] = useState(null);
  const [form, setForm] = useState({
    kind: types[0]?.key || "malattia",
    start_date: today,
    end_date: today,
    reason: "",
    hours: "",
    as_proposal: false,
  });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/absences", { params: { scope: "mine" } }),
        api.get(`/operators/${user.id}/balance`),
      ]);
      setList(a.data);
      setBalance(b.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  const needsHours = form.kind === "permesso" || form.kind === "recupero";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        kind: form.kind,
        start_date: form.start_date,
        end_date: form.end_date,
        reason: form.reason,
        as_proposal: form.as_proposal && form.kind === "ferie",
      };
      if (needsHours && form.hours) payload.hours = Number(form.hours);
      await api.post("/absences", payload);
      toast.success(payload.as_proposal ? "Proposta salvata" : "Richiesta inviata");
      setForm({ ...form, reason: "", hours: "" });
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const removeAbsence = async (id) => {
    try {
      await api.delete(`/absences/${id}`);
      toast.success("Richiesta eliminata");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="absences-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold">Assenze</h1>
      </div>

      {/* Monte ferie/permessi */}
      {balance && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5" data-testid="operator-balance">
          <div className="flex items-center gap-2 mb-3">
            <Wallet className="h-4 w-4 text-emerald-600" />
            <h2 className="text-sm font-semibold">Il tuo monte ({balance.year})</h2>
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-xs text-gray-500">Ferie residue</p>
              <p data-testid="my-vacation" className="font-mono-num text-2xl font-bold mt-1">
                {balance.vacation.residual_days}
                <span className="text-sm text-gray-500 ml-1">/ {balance.vacation.total_days} gg</span>
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Permessi residui</p>
              <p data-testid="my-permit" className="font-mono-num text-2xl font-bold mt-1">
                {balance.permit.residual_hours}
                <span className="text-sm text-gray-500 ml-1">/ {balance.permit.total_hours} h</span>
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Banca ore recupero</p>
              <p data-testid="my-bank" className="font-mono-num text-2xl font-bold mt-1 text-violet-700">
                {fmtHours(balance.recovery.bank_minutes)}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        <form onSubmit={submit} className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-base font-semibold">Nuova richiesta</h2>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-600">Tipo</label>
            <select
              data-testid="absence-kind"
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value, as_proposal: e.target.value !== "ferie" ? false : form.as_proposal })}
              className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
            >
              {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-600">Dal</label>
              <input
                data-testid="absence-start"
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-600">Al</label>
              <input
                data-testid="absence-end"
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              />
            </div>
          </div>
          {needsHours && (
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-600">
                Ore {form.kind === "permesso" ? "permesso" : "recupero"}
              </label>
              <input
                data-testid="absence-hours"
                type="number"
                step="0.5"
                min="0"
                value={form.hours}
                onChange={(e) => setForm({ ...form, hours: e.target.value })}
                className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm font-mono-num focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                placeholder="Es. 2.5"
              />
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-600">Note</label>
            <textarea
              data-testid="absence-reason"
              rows={3}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="mt-1 w-full rounded-lg border border-gray-200 p-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="Opzionale"
            />
          </div>
          {form.kind === "ferie" && (
            <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="absence-proposal-toggle">
              <input
                type="checkbox"
                checked={form.as_proposal}
                onChange={(e) => setForm({ ...form, as_proposal: e.target.checked })}
                className="mt-0.5 h-4 w-4 accent-emerald-600"
              />
              <span>
                <span className="font-semibold">Pianifica come proposta</span>
                <span className="block text-xs text-gray-500">Il titolare vede la proposta senza consumo del monte ferie. Verrà consumato solo dopo l'approvazione.</span>
              </span>
            </label>
          )}
          <button
            data-testid="absence-request-submit"
            disabled={busy}
            className="w-full h-12 rounded-lg bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2 transition-colors hover:bg-emerald-700 active:scale-95 disabled:opacity-60"
          >
            {busy ? "Invio..." : <><Send className="h-4 w-4" /> {form.as_proposal && form.kind === "ferie" ? "Salva proposta" : "Invia richiesta"}</>}
          </button>
        </form>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-testid="my-absences-list">
          <h2 className="text-base font-semibold">Le mie richieste</h2>
          {list.length === 0 ? (
            <p className="text-sm text-gray-600 mt-4">Nessuna richiesta inviata.</p>
          ) : (
            <table className="w-full mt-4 text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-gray-600">
                  <th className="pb-3">Tipo</th><th className="pb-3">Periodo</th><th className="pb-3">Ore</th><th className="pb-3">Note</th><th className="pb-3">Stato</th><th className="pb-3"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((a) => (
                  <tr key={a.id} data-testid={`absence-row-${a.id}`} className="border-t border-gray-100">
                    <td className="py-3"><KindBadge type={types.find((t) => t.key === a.kind) || { key: a.kind }} /></td>
                    <td className="py-3">{fmtDate(a.start_date)} – {fmtDate(a.end_date)}</td>
                    <td className="py-3 font-mono-num text-gray-600">{a.hours ? `${a.hours}h` : "—"}</td>
                    <td className="py-3 text-gray-600">{a.reason || "—"}</td>
                    <td className="py-3"><StatusBadge status={a.status} /></td>
                    <td className="py-3 text-right">
                      {(a.status === "proposal" || a.status === "pending") && (
                        <button
                          data-testid={`absence-delete-${a.id}`}
                          onClick={() => removeAbsence(a.id)}
                          aria-label="Elimina"
                          className="h-8 w-8 rounded-lg border border-red-200 text-red-600 inline-flex items-center justify-center hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
